import React from 'react';
import { Box, styled } from '@mui/material';
import { ReactFlow, MiniMap, Controls } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Row } from 'ui/Flex';
import { MuiForm } from 'lib/Form';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { RuleSearchType } from '../rules.types';
import { automationNodeTypes, AutomationSearchFormValues } from './automations.types';
import { useAutomationSearch } from './hooks/useAutomationSearch';
import { useHistoryFilters } from 'lib/hooks/useHistoryFilters';
import { isEmptyString, isString } from 'lib/typeguards';
import { useHistory } from 'react-router-dom';
import { Throbber } from 'ui/Throbber';

export const ruleSearchTypeOptions = [
    { label: 'Perk', value: RuleSearchType.Perk },
    { label: 'Audience', value: RuleSearchType.Audience }
];

export const proOptions = { hideAttribution: true };

const FlowWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    backgroundColor: theme.palette.background.paper
}));

function isValidSearchType(value: unknown): value is RuleSearchType {
    return isString(value) && new Set<string>(Object.values(RuleSearchType)).has(value);
}

export const Automations: React.FC = () => {
    const { perks, loading: perksLoading } = useSelector((state: ApplicationState) => state.perks);
    const { audiences, isLoading: audiencesLoading } = useSelector(
        (state: ApplicationState) => state.audiences
    );
    const { replace } = useHistory();
    const { search: urlParams, onSearch } = useHistoryFilters();
    const [initiated, setInitiated] = React.useState(false);

    const initialFormValues: AutomationSearchFormValues = React.useMemo(() => {
        const _isValidSearchType = isValidSearchType(urlParams.type);
        const type = _isValidSearchType ? urlParams.type : RuleSearchType.Audience;
        const search = _isValidSearchType && isString(urlParams.search) ? urlParams.search : '';
        return { search, type };
    }, [urlParams.search, urlParams.type]);

    const { handleSubmitSearch, isRulesLoading, nodes, edges } = useAutomationSearch(onSearch);

    React.useEffect(() => {
        if (!perksLoading && !audiencesLoading && (!initiated || urlParams.refresh === 'true')) {
            if (
                isString(urlParams.search) &&
                !isEmptyString(urlParams.search) &&
                isValidSearchType(urlParams.type)
            ) {
                if (urlParams.refresh === 'true') {
                    replace(`${location.pathname}?type=${urlParams.type}&search=${urlParams.search}`);
                }
                handleSubmitSearch({ type: urlParams.type, search: urlParams.search });
            }
            if (!initiated) {
                setInitiated(true);
            }
        }
    }, [
        handleSubmitSearch,
        initiated,
        perksLoading,
        audiencesLoading,
        urlParams.search,
        urlParams.type,
        urlParams.refresh,
        replace
    ]);

    const perkOptions = React.useMemo(() => {
        const options = perks.map(perk => ({ label: `${perk.title} [${perk._id}]`, value: perk._id }));
        if (urlParams.type === RuleSearchType.Perk) {
            if (perks.find(perk => perk._id === urlParams.search)) {
                return options;
            }
            return [...options, { label: `Unknown perk [${urlParams.search}]`, value: urlParams.search }];
        }
        return options;
    }, [perks, urlParams.search, urlParams.type]);
    const audienceOptions = React.useMemo(() => {
        const options = audiences.map(audience => ({
            label: `${audience.title} [${audience._id}]`,
            value: audience._id
        }));
        if (urlParams.type === RuleSearchType.Audience) {
            if (audiences.find(audience => audience._id === urlParams.search)) {
                return options;
            }
            return [...options, { label: `Unknown Audience [${urlParams.search}]`, value: urlParams.search }];
        }
        return options;
    }, [audiences, urlParams.search, urlParams.type]);

    return (
        <Box width="100%" height="100%" display="flex" flexDirection="column">
            <Box mb={1}>
                <MuiForm initialValues={initialFormValues} onSubmit={handleSubmitSearch} enableReinitialize>
                    {({ submitForm, isSubmitting, values: { type: searchType, search } }) => (
                        <Row gutter>
                            <SelectFormField
                                variant="outlined"
                                name="type"
                                options={ruleSearchTypeOptions}
                                size="small"
                            />
                            {searchType === RuleSearchType.Perk && (
                                <AutocompleteFormField
                                    textFieldVariant="outlined"
                                    size="small"
                                    name="search"
                                    options={perkOptions}
                                    disabled={perksLoading}
                                />
                            )}
                            {searchType === RuleSearchType.Audience && (
                                <AutocompleteFormField
                                    textFieldVariant="outlined"
                                    size="small"
                                    name="search"
                                    options={audienceOptions}
                                    disabled={audiencesLoading}
                                />
                            )}
                            <LoadingButton
                                variant="contained"
                                onClick={submitForm}
                                loading={isSubmitting || audiencesLoading || perksLoading || isRulesLoading}
                                disabled={isSubmitting || !search || !searchType || isRulesLoading}
                            >
                                Search
                            </LoadingButton>
                        </Row>
                    )}
                </MuiForm>
            </Box>
            <Box width="100%" flex={1}>
                {isRulesLoading ? (
                    <Throbber />
                ) : (
                    <FlowWrapper>
                        <ReactFlow
                            nodesConnectable={false}
                            nodes={nodes}
                            edges={edges}
                            fitView
                            nodeTypes={automationNodeTypes}
                            proOptions={proOptions}
                            edgesFocusable={false}
                        >
                            <MiniMap zoomable pannable />
                            <Controls showInteractive={false} />
                        </ReactFlow>
                    </FlowWrapper>
                )}
            </Box>
        </Box>
    );
};
